"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";

import Section3Page from "./path/section3";
import DesertPage from "./path/desert/page";
import LuxuryPage from "./path/page/page";

// ──────────────────────────────────────
// SEGMENTED RING (R3F Helper)
// ──────────────────────────────────────
const SegmentedRing = React.forwardRef<THREE.Group, { r: number; tube: number; color: number; gapDeg?: number }>(({ r, tube, color, gapDeg = 9 }, ref) => {
  const arcRad = ((90 - gapDeg * 2) * Math.PI) / 180;
  const halfGapR = (gapDeg * Math.PI) / 180;

  const segments = useMemo(() => {
    return [0, 1, 2, 3].map((i) => {
      const startRad = (i * Math.PI) / 2 + halfGapR;
      const geo = new THREE.TorusGeometry(r, tube, 14, 220, arcRad);
      return { geo, startRad, key: i };
    });
  }, [r, tube, gapDeg, arcRad, halfGapR]);

  return (
    <group ref={ref}>
      {segments.map(({ geo, startRad, key }) => (
        <mesh key={key} geometry={geo} rotation-z={startRad}>
          <meshStandardMaterial color={color} metalness={0.88} roughness={0.28} />
        </mesh>
      ))}
    </group>
  );
});
SegmentedRing.displayName = "SegmentedRing";

// ──────────────────────────────────────
// 3D LENS SCENE COMPONENT
// ──────────────────────────────────────
interface LensSceneProps {
  labelDomRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  activeHoverLabel: string | null;
}

function LensScene({ labelDomRefs, activeHoverLabel }: LensSceneProps) {
  const lensGroupRef = useRef<THREE.Group>(null!);
  const outerSegRef = useRef<THREE.Group>(null!);
  const midGroupRef = useRef<THREE.Group>(null!);
  const innerSegRef = useRef<THREE.Group>(null!);
  const deepSegRef = useRef<THREE.Group>(null!);
  const sphereRef = useRef<THREE.Mesh>(null!);

  const tempVec = useMemo(() => new THREE.Vector3(), []);

  const labelAnchors = useMemo<Record<string, THREE.Vector3>>(
    () => ({
      "lbl-tech": new THREE.Vector3(0, 4.55, 0),
      "lbl-communities": new THREE.Vector3(4.6, 0, 0),
      "lbl-media": new THREE.Vector3(-4.6, 0, 0),
      "lbl-products": new THREE.Vector3(0, -3.15, 0),
      "lbl-orca": new THREE.Vector3(0, -2.0, 0),
      "lbl-venom": new THREE.Vector3(3.2, 3.2, 0),
      "lbl-beem": new THREE.Vector3(-3.2, 3.2, 0),
      "lbl-fluid": new THREE.Vector3(-3.2, -3.2, 0),
      "lbl-building": new THREE.Vector3(3.2, -3.2, 0),
      "lbl-jeruk": new THREE.Vector3(0, 0, 0)
    }),
    []
  );

  useEffect(() => {
    if (!sphereRef.current) return;
    const loader = new THREE.TextureLoader();
    loader.load(
      "./ChatGPT Image 5 Jun 2026, 02.47.16.png",
      (tex) => {
        tex.mapping = THREE.CubeReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        if (sphereRef.current && sphereRef.current.material) {
          const mat = sphereRef.current.material as THREE.MeshStandardMaterial;
          mat.map = tex;
          mat.envMap = tex;
          mat.needsUpdate = true;
        }
      },
      undefined,
      () => {}
    );
  }, []);

  useEffect(() => {
    const forwardZ = 1.0;
    const backwardZ = -1.2;

    const hoverTargets: Record<string, THREE.Object3D | null> = {
      "lbl-tech": outerSegRef.current,
      "lbl-communities": midGroupRef.current,
      "lbl-media": innerSegRef.current,
      "lbl-products": sphereRef.current,
      "lbl-orca": deepSegRef.current,
      "lbl-venom": outerSegRef.current,
      "lbl-beem": innerSegRef.current,
      "lbl-fluid": midGroupRef.current,
      "lbl-building": deepSegRef.current,
      "lbl-jeruk": sphereRef.current
    };

    for (const [id, obj] of Object.entries(hoverTargets)) {
      if (!obj) continue;
      const isCurrent = id === activeHoverLabel;
      const baselineZ = obj === sphereRef.current ? 0.2 : 0.0;
      const targetZ = activeHoverLabel === null ? baselineZ : isCurrent ? baselineZ + forwardZ : baselineZ + backwardZ;

      gsap.to(obj.position, {
        z: targetZ,
        duration: 0.8,
        ease: "power2.out",
        overwrite: "auto"
      });

      if (obj !== sphereRef.current) {
        const targetColor = activeHoverLabel === null ? new THREE.Color(0x000000) : isCurrent ? new THREE.Color(0xfff5ea).multiplyScalar(0.45) : new THREE.Color(0x000000);

        obj.traverse((child) => {
          if ((child as THREE.Mesh).isMesh && (child as THREE.Mesh).material) {
            const mat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
            if (mat && mat.emissive) {
              gsap.to(mat.emissive, {
                r: targetColor.r,
                g: targetColor.g,
                b: targetColor.b,
                duration: 0.8,
                ease: "power2.out",
                overwrite: "auto"
              });
            }
          }
        });
      } else {
        const mat = (sphereRef.current as THREE.Mesh).material as THREE.MeshStandardMaterial;
        if (mat) {
          gsap.to(mat, {
            roughness: activeHoverLabel === null ? 0.51 : isCurrent ? 0.2 : 0.8,
            duration: 0.8,
            overwrite: "auto"
          });
        }
      }
    }
  }, [activeHoverLabel]);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();

    // ── Responsive camera: zoom out otomatis agar lens + label selalu masuk layar
    const cam = state.camera as THREE.PerspectiveCamera;
    const aspect = state.size.width / state.size.height;
    const halfFov = THREE.MathUtils.degToRad(cam.fov) / 2;
    const fitZ = Math.max(12.5, 5.4 / (Math.tan(halfFov) * aspect), 5.15 / Math.tan(halfFov));
    cam.position.z = THREE.MathUtils.lerp(cam.position.z, fitZ, 0.07);

    if (outerSegRef.current) outerSegRef.current.rotation.z = t * 0.048;
    if (midGroupRef.current) midGroupRef.current.rotation.z = -t * 0.027;
    if (innerSegRef.current) innerSegRef.current.rotation.z = t * 0.018;
    if (sphereRef.current) sphereRef.current.rotation.y = t * 0.38;
    if (lensGroupRef.current) lensGroupRef.current.position.y = Math.sin(t * 0.35) * 0.04;

    const w = state.size.width;
    const h = state.size.height;
    for (const [id, v3] of Object.entries(labelAnchors)) {
      const el = labelDomRefs.current[id];
      if (el) {
        tempVec.copy(v3).project(state.camera);
        el.style.left = `${((tempVec.x + 1) / 2) * w}px`;
        el.style.top = `${((-tempVec.y + 1) / 2) * h}px`;
      }
    }
  });

  return (
    <>
      <ambientLight intensity={1.0} color={0xffffff} />
      <directionalLight intensity={3.2} color={0xffffff} position={[5, 9, 7]} />
      <directionalLight intensity={0.9} color={0x4466bb} position={[-6, 2, 5]} />
      <directionalLight intensity={0.6} color={0x334455} position={[0, -6, -4]} />
      <pointLight intensity={1.8} color={0x889aaa} distance={20} decay={1} position={[0, 0, 8]} />

      <group ref={lensGroupRef}>
        <mesh>
          <torusGeometry args={[4.7, 0.022, 14, 240]} />
          <meshStandardMaterial color={0x252525} metalness={0.9} roughness={0.2} />
        </mesh>

        <SegmentedRing ref={outerSegRef} r={4.22} tube={0.33} color={0x1c1c1c} gapDeg={9} />

        <mesh>
          <torusGeometry args={[3.85, 0.04, 14, 240]} />
          <meshStandardMaterial color={0x3a3a3a} metalness={0.88} roughness={0.24} />
        </mesh>

        <group ref={midGroupRef}>
          <mesh>
            <torusGeometry args={[3.5, 0.23, 14, 240]} />
            <meshStandardMaterial color={0x161616} metalness={0.85} roughness={0.32} />
          </mesh>
          <mesh>
            <torusGeometry args={[3.18, 0.03, 14, 240]} />
            <meshStandardMaterial color={0x2c2c2c} metalness={0.88} roughness={0.26} />
          </mesh>
        </group>

        <SegmentedRing ref={deepSegRef} r={1.08} tube={0.22} color={0x1c1c1c} gapDeg={12} />
        <SegmentedRing ref={innerSegRef} r={2.8} tube={0.22} color={0x121212} gapDeg={8} />

        <mesh>
          <torusGeometry args={[2.48, 0.03, 14, 240]} />
          <meshStandardMaterial color={0x333333} metalness={0.85} roughness={0.3} />
        </mesh>
        <mesh>
          <torusGeometry args={[1.96, 0.03, 14, 240]} />
          <meshStandardMaterial color={0x292929} metalness={0.85} roughness={0.3} />
        </mesh>

        <mesh>
          <torusGeometry args={[1.43, 0.07, 16, 240]} />
          <meshStandardMaterial color={0x454545} metalness={0.9} roughness={0.2} />
        </mesh>

        <mesh position={[0, 0, -0.08]}>
          <circleGeometry args={[1.36, 128]} />
          <meshStandardMaterial color={0x030303} metalness={0.3} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>

        <mesh ref={sphereRef} position={[0, 0, 0.2]}>
          <sphereGeometry args={[1.09, 64, 64]} />
          <meshStandardMaterial color={0xb9afa5} metalness={0.0} roughness={0.51} />
        </mesh>
      </group>
    </>
  );
}

// ──────────────────────────────────────
// MAIN PORTAL COMPONENT
// ──────────────────────────────────────
export default function Portal() {
  const [activeRoute, setActiveRoute] = useState<string>("portal");

  const [activeHoverLabel, setActiveHoverLabel] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [timeStr, setTimeStr] = useState("( LA ) 22:41");
  const [coordsText, setCoordsText] = useState("34.0522° N, 118.2437° W");

  const labelDomRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuOverlayRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLDivElement>(null);
  const menuTimeline = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const updateTime = () => {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "America/Los_Angeles",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      };
      const formatter = new Intl.DateTimeFormat([], options);
      setTimeStr(`( LA )  ${formatter.format(new Date())}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isMenuOpen) {
        const x = ((e.clientX / window.innerWidth) * 180 - 90).toFixed(4);
        const y = ((e.clientY / window.innerHeight) * 90 - 45).toFixed(4);
        const numX = Number(x);
        const numY = Number(y);
        setCoordsText(`${Math.abs(numX)}° ${numX >= 0 ? "N" : "S"}, ${Math.abs(numY)}° ${numY >= 0 ? "E" : "W"}`);
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMenuOpen]);

  const navigateTo = (page: string) => {
    if (overlayRef.current) {
      overlayRef.current.style.opacity = "1";
      overlayRef.current.style.pointerEvents = "all";
    }
    setTimeout(() => {
      const clean = page.replace(".html", "").replace("./", "");
      if (clean.startsWith("/")) {
        window.location.href = clean;
        return;
      }
      const routeMap: Record<string, string> = {
        section2: "/portofolio/model/path/section2",
        section3: "/portofolio/model/path/section3",
        desert: "/portofolio/model/path/desert",
        page: "/portofolio/model/path/page",
        section: "/portofolio/model/path/section",
        orca: "/portofolio/model/path/orca",
        venom: "/portofolio/model/path/venom",
        beem: "/portofolio/model/path/beem",
        fluid: "/portofolio/model/path/fluid",
        building: "/portofolio/model/path/building",
        "jeruk-limau": "/portofolio/model/path/jeruk-limau",
        main: "/portofolio/model/path/main"
      };
      window.location.href = routeMap[clean] ?? `/portofolio/model/path/${clean}`;
    }, 500);
  };

  const openMenu = () => {
    setIsMenuOpen(true);
    if (menuTimeline.current) menuTimeline.current.kill();
    const tl = gsap.timeline();
    menuTimeline.current = tl;

    tl.to(menuOverlayRef.current, {
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      duration: 1.1,
      ease: "power4.inOut"
    });

    tl.fromTo(
      "#menu-overlay .char-inner",
      { y: "105%", rotateX: -15 },
      {
        y: "0%",
        rotateX: 0,
        duration: 1.0,
        ease: "power3.out",
        stagger: 0.02
      },
      "-=0.6"
    );

    tl.fromTo(
      "#menu-overlay .menu-meta-item",
      { opacity: 0, y: 15 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.08
      },
      "-=0.7"
    );

    tl.fromTo(closeBtnRef.current, { opacity: 0 }, { opacity: 0.6, duration: 0.5 }, "-=0.8");
  };

  const closeMenu = (targetPage?: string) => {
    if (menuTimeline.current) menuTimeline.current.kill();
    const tl = gsap.timeline({
      onComplete: () => {
        setIsMenuOpen(false);
        if (targetPage) {
          navigateTo(targetPage);
        }
      }
    });
    menuTimeline.current = tl;

    tl.to("#menu-overlay .char-inner", {
      y: "-105%",
      duration: 0.6,
      ease: "power3.in",
      stagger: 0.01
    });

    tl.to("#menu-overlay .menu-meta-item", { opacity: 0, y: -10, duration: 0.5, stagger: 0.05 }, 0);

    tl.to(closeBtnRef.current, { opacity: 0, duration: 0.3 }, 0);

    tl.to(
      menuOverlayRef.current,
      {
        clipPath: "polygon(0 100%, 100% 100%, 100% 100%, 0 100%)",
        duration: 0.9,
        ease: "power4.inOut",
        onComplete: () => {
          gsap.set(menuOverlayRef.current, { clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)" });
        }
      },
      "-=0.5"
    );
  };

  const renderSplitText = (text: string) => {
    return [...text].map((char, index) => (
      <span key={index} className='char-wrap'>
        <span className='char-inner'>{char === " " ? "\u00A0" : char}</span>
      </span>
    ));
  };

  const menuNavItems = [
    { number: "01", text: "TECHNOLOGY", href: "section2", active: activeRoute === "section2" },
    { number: "02", text: "COMMUNITIES", href: "section3", active: activeRoute === "section3" },
    { number: "03", text: "MEDIA", href: "desert", active: activeRoute === "desert" },
    { number: "04", text: "PRODUCTS", href: "page", active: activeRoute === "page" },
    { number: "05", text: "ORCA", href: "orca", active: activeRoute === "orca" },
    { number: "06", text: "VENOM", href: "venom", active: activeRoute === "venom" },
    { number: "07", text: "BEEM EDITOR", href: "beem", active: activeRoute === "beem" },
    { number: "08", text: "FLUID", href: "fluid", active: activeRoute === "fluid" },
    { number: "09", text: "BUILDING", href: "building", active: activeRoute === "building" },
    { number: "10", text: "JERUK LIMAU", href: "jeruk-limau", active: activeRoute === "jeruk-limau" }
  ];

  if (activeRoute === "section3") return <Section3Page />;
  if (activeRoute === "desert") return <DesertPage />;
  if (activeRoute === "page") return <LuxuryPage />;

  return (
    <div className='relative w-full h-dvh overflow-hidden bg-black text-white font-sans select-none'>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Syncopate:wght@700&display=swap');

        .font-syncopate {
          font-family: 'Syncopate', sans-serif;
        }

        .sound-bar {
          width: 2px;
          background: #fff;
          border-radius: 1px;
          animation: sb .85s ease-in-out infinite alternate;
        }
        .sound-bar:nth-child(1) { height: 30%; animation-delay: 0s; }
        .sound-bar:nth-child(2) { height: 100%; animation-delay: .2s; }
        .sound-bar:nth-child(3) { height: 55%; animation-delay: .1s; }
        .sound-bar:nth-child(4) { height: 75%; animation-delay: .3s; }

        @keyframes sb {
          0% { transform: scaleY(.25); }
          100% { transform: scaleY(1); }
        }

        .label {
          position: absolute;
          font-size: clamp(6px, 1.4vmin, 10px);
          font-weight: 700;
          letter-spacing: .16em;
          text-transform: uppercase;
          white-space: nowrap;
          user-select: none;
          color: rgba(255, 255, 255, .70);
          transition: color .35s, text-shadow .35s, letter-spacing .35s;
          pointer-events: auto;
          cursor: default;
        }

        .label[data-page] {
          cursor: pointer;
        }

        .label[data-page]:hover::after {
          content: ' ↗';
          opacity: .6;
          font-size: 9px;
        }

        .label.glowing {
          color: rgba(255, 255, 255, 1.0);
          letter-spacing: .20em;
          text-shadow: 0 0 12px rgba(255, 220, 90, .7), 0 0 30px rgba(255, 180, 60, .4);
        }

        #lbl-tech { transform: translate(-50%, -100%); }
        #lbl-products { transform: translate(-50%, 20px); }
        #lbl-retro { text-align: center; transform: translate(-50%, -100px); }
        #lbl-communities { writing-mode: vertical-lr; transform: translate(20px, -50%); }
        #lbl-media { writing-mode: vertical-lr; transform: translate(-100%, -50%) rotate(180deg); }

        .menu-overlay {
          position: fixed;
          inset: 0;
          background: rgba(3, 3, 3, 0.97);
          backdrop-filter: blur(25px);
          -webkit-backdrop-filter: blur(25px);
          z-index: 2000;
          clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 60px 10%;
          pointer-events: none;
        }

        .menu-overlay.active {
          pointer-events: auto;
        }

        .menu-link::before {
          content: attr(data-number);
          font-family: 'Inter', sans-serif;
          font-size: clamp(10px, 1.2vw, 14px);
          font-weight: 700;
          letter-spacing: .1em;
          color: rgba(255, 255, 255, 0.15);
          margin-top: 6px;
          align-self: flex-start;
          transition: color 0.4s;
        }

        .menu-link:hover {
          color: #fff;
        }

        .menu-link:hover::before {
          color: rgba(255, 255, 255, 0.6);
        }

        .char-wrap {
          display: inline-block;
          overflow: hidden;
          vertical-align: bottom;
        }

        .char-inner {
          display: inline-block;
          transform: translateY(105%);
          transition: transform 0.5s cubic-bezier(0.215, 0.61, 0.355, 1);
        }

        .menu-nav:hover .menu-link:not(:hover) {
          opacity: 0.35;
        }

        .menu-close {
          position: absolute;
          top: 40px;
          right: 40px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 11px;
          letter-spacing: .15em;
          font-weight: 700;
          cursor: pointer;
          opacity: .6;
          transition: opacity .3s, transform .3s;
          z-index: 2;
        }
        .menu-close:hover { opacity: 1; transform: scale(1.05); }
        .close-icon { position: relative; width: 16px; height: 16px; }
        .close-icon .line { position: absolute; top: 8px; left: 0; width: 100%; height: 1.5px; background: #fff; transition: transform .3s; }
        .close-icon .line-1 { transform: rotate(45deg); }
        .close-icon .line-2 { transform: rotate(-45deg); }

        .menu-container {
          display: grid;
          grid-template-columns: 4.5fr 7.5fr;
          gap: 80px;
          align-items: center;
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
        }
        .menu-left { display: flex; flex-direction: column; gap: 36px; border-right: 1px solid rgba(255,255,255,0.08); padding-right: 60px; }
        .menu-meta-item { display: flex; flex-direction: column; gap: 10px; }
        .meta-label { font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,.35); }
        .meta-value { font-size: 12px; letter-spacing: .05em; color: rgba(255,255,255,.6); }

        /* ── RESPONSIVE MOBILE ── */
        @media (max-width: 768px) {
          .menu-overlay {
            padding: 72px 6% 32px;
            justify-content: flex-start;
            overflow-y: auto;
          }
          .menu-close { top: 20px; right: 18px; font-size: 10px; }
          .menu-container { grid-template-columns: 1fr; gap: 0; }
          .menu-left { display: none; }
          .menu-nav { max-height: none !important; gap: 10px; }
          .menu-link { font-size: clamp(15px, 5.5vw, 24px) !important; }
          .menu-link::before { font-size: 9px !important; }
        }

        @media (max-width: 480px) {
          .menu-overlay { padding-top: 64px; }
          .menu-link { font-size: clamp(14px, 6vw, 20px) !important; }
        }
      `}</style>

      {/* 3D Canvas Layer */}
      <Canvas
        className='fixed inset-0 w-full h-full block'
        camera={{ fov: 42, position: [0, 0.4, 12.5] }}
        gl={{
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.2
        }}
      >
        <LensScene labelDomRefs={labelDomRefs} activeHoverLabel={activeHoverLabel} />
      </Canvas>

      {/* MENU OVERLAY */}
      <div ref={menuOverlayRef} id='menu-overlay' className={`menu-overlay ${isMenuOpen ? "active" : ""}`}>
        <div ref={closeBtnRef} className='menu-close' onClick={() => closeMenu()}>
          <span>CLOSE</span>
          <div className='close-icon'>
            <div className='line line-1'></div>
            <div className='line line-2'></div>
          </div>
        </div>

        <div className='menu-container'>
          <div className='menu-left'>
            <div className='menu-meta-item'>
              <div className='meta-label'>SECTIONS</div>
              <div className='meta-value'>01 / CREATOR TECHNOLOGY</div>
              <div className='meta-value'>02 / CREATOR COMMUNITIES</div>
              <div className='meta-value'>03 / CREATOR MEDIA</div>
              <div className='meta-value'>04 / CREATOR PRODUCTS</div>
              <div className='meta-value'>05 / ORCA SHOWCASE</div>
              <div className='meta-value'>06 / VENOM</div>
              <div className='meta-value'>07 / BEEM EDITOR</div>
              <div className='meta-value'>08 / FLUID SIM</div>
              <div className='meta-value'>09 / BUILDING</div>
              <div className='meta-value'>10 / JERUK LIMAU</div>
            </div>

            <div className='menu-meta-item'>
              <div className='meta-label'>COORDINATES & TIME</div>
              <div className='meta-value'>{coordsText}</div>
              <div className='meta-value'>{timeStr}</div>
            </div>

            <div className='menu-meta-item'>
              <div className='meta-label'>MISSION</div>
              <div className='meta-value'>BUILDING THE OPERATING SYSTEM FOR THE CREATOR ECONOMY. EMPOWERING COMMUNITIES AND MEDIA NARRATIVES GLOBALLY.</div>
            </div>
          </div>

          <nav className='menu-nav' style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {menuNavItems.map((item) => (
              <div key={item.number} className='menu-item-wrap'>
                <a
                  href={`/portofolio/model/path/${item.href}`}
                  className={`menu-link ${item.active ? "active" : ""}`}
                  data-number={item.number}
                  style={{ fontSize: "clamp(18px, 2.8vw, 40px)" }}
                  onClick={(e) => {
                    e.preventDefault();
                    closeMenu(item.href);
                  }}
                >
                  <span className='menu-link-text'>{renderSplitText(item.text)}</span>
                </a>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* UI OVERLAY */}
      <div id='ui' className='fixed inset-0 pointer-events-none z-10'>
        {/* NAV */}
        <nav className='nav absolute top-0 left-0 right-0 px-4 py-4 md:px-8 md:py-6 flex justify-between items-center pointer-events-auto'>
          <div className='nav-logo text-[10px] md:text-xs tracking-widest font-bold'>PROJECT 3D</div>
          <div className='nav-center text-[10px] tracking-wider opacity-45 hidden sm:block'>{timeStr}</div>
          <div className='nav-right flex items-center gap-2.5 md:gap-3.5 text-xs tracking-widest font-bold'>
            <div className='sound-bars flex items-end gap-[2px] h-[10px] md:h-[14px]'>
              <div className='sound-bar'></div>
              <div className='sound-bar'></div>
              <div className='sound-bar'></div>
              <div className='sound-bar'></div>
            </div>
            <div id='menu' className='cursor-pointer text-[11px] md:text-xs' onClick={openMenu}>
              MENU
            </div>
          </div>
        </nav>

        {/* CROSSHAIR */}
        <div className='xhair absolute left-0 right-0 top-1/2 h-[1px] -translate-y-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent'></div>

        {/* 3D INTERACTIVE LABELS */}
        <div id='labels' className='absolute inset-0'>
          {(
            [
              { id: "lbl-tech", page: "section2", label: "CREATOR TECHNOLOGY", style: { transform: "translate(-50%, -100%)" } },
              { id: "lbl-communities", page: "section3", label: "CREATOR COMMUNITIES", style: { writingMode: "vertical-lr" as const, transform: "translate(20px, -50%)" } },
              { id: "lbl-media", page: "desert", label: "CREATOR MEDIA", style: { writingMode: "vertical-lr" as const, transform: "translate(-100%, -50%) rotate(180deg)" } },
              { id: "lbl-products", page: "page", label: "CREATOR PRODUCTS", style: { transform: "translate(-50%, 20px)" } },
              { id: "lbl-orca", page: "orca", label: "ORCA", style: { transform: "translate(-50%, -100px)" } },
              { id: "lbl-venom", page: "venom", label: "VENOM", style: { transform: "translate(-50%, -100%)" } },
              { id: "lbl-beem", page: "beem", label: "BEEM EDITOR", style: { transform: "translate(-100%, -50%)" } },
              { id: "lbl-fluid", page: "fluid", label: "FLUID SIM", style: { transform: "translate(-100%, 20px)" } },
              { id: "lbl-building", page: "building", label: "BUILDING", style: { transform: "translate(20px, 20px)" } },
              { id: "lbl-jeruk", page: "jeruk-limau", label: "JERUK LIMAU", style: { transform: "translate(-50%, -50%)" } }
            ] as const
          ).map(({ id, page, label, style }) => (
            <div
              key={id}
              id={id}
              ref={(el) => {
                labelDomRefs.current[id] = el;
              }}
              data-page={page}
              className={`label ${activeHoverLabel === id ? "glowing" : ""}`}
              style={style}
              onMouseEnter={() => setActiveHoverLabel(id)}
              onMouseLeave={() => setActiveHoverLabel(null)}
              onClick={() => navigateTo(page)}
            >
              {label}
            </div>
          ))}
        </div>

        {/* HEADLINE */}
        <div className='headline absolute bottom-[24px] left-[20px] md:bottom-[52px] md:left-[42px] pointer-events-auto'>
          <h1 className='font-syncopate text-[10px] sm:text-base md:text-xl font-bold uppercase leading-tight tracking-wide'>
            THE OPERATING SYSTEM
            <br />
            FOR THE CREATOR
            <br />
            ECONOMY.
          </h1>
          <div className='about-btn hidden md:flex items-center gap-3 mt-5 cursor-pointer opacity-75 hover:opacity-100 transition-opacity text-[10px] tracking-widest uppercase font-semibold'>
            <span>About Us</span>
            <div className='about-circle w-7 h-7 border border-white/40 rounded-full flex items-center justify-center text-sm leading-none'>›</div>
          </div>
        </div>
      </div>

      {/* Page transition overlay */}
      <div ref={overlayRef} id='overlay' className='fixed inset-0 bg-black opacity-0 pointer-events-none transition-opacity duration-700 z-[1000]'></div>
    </div>
  );
}
